import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { AccountFinal } from '@/api/entities';
import { toast } from 'sonner';

export default function AccountDataExporter() {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Fetch all accounts across all tenants
      const allAccounts = await AccountFinal.list();
      
      // Filter and map to desired fields
      const exportData = allAccounts.map(account => ({
        account_name: account.account_name || '',
        account_id: account.account_id || '',
        tenant_id: account.tenant_id || '',
        incumbent_sentiment: account.incumbent_sentiment || '',
        incumbent_sentiment_rationale: account.incumbent_sentiment_rationale || '',
        decision_window_band: account.decision_window_band || '',
        decision_window_rationale: account.decision_window_rationale || ''
      }));

      // Create CSV headers
      const headers = [
        'account_name',
        'account_id',
        'tenant_id',
        'incumbent_sentiment',
        'incumbent_sentiment_rationale',
        'decision_window_band',
        'decision_window_rationale'
      ];

      // Convert to CSV
      const csvRows = [];
      csvRows.push(headers.join(','));

      exportData.forEach(row => {
        const values = headers.map(header => {
          const value = row[header] || '';
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          const escaped = String(value).replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        });
        csvRows.push(values.join(','));
      });

      const csvContent = csvRows.join('\n');
      
      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `account_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} accounts to CSV`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export accounts: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="w-5 h-5" />
          Export Account Data
        </CardTitle>
        <CardDescription>
          Export account data across all tenants to CSV (admin only)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will export the following fields for all accounts across all tenants:
          </p>
          <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
            <li>Account Name</li>
            <li>Account ID</li>
            <li>Tenant ID</li>
            <li>Incumbent Sentiment</li>
            <li>Incumbent Sentiment Rationale</li>
            <li>Decision Window Band</li>
            <li>Decision Window Rationale</li>
          </ul>
          <Button 
            onClick={handleExport} 
            disabled={isExporting}
            className="w-full bg-[#0A1A2F] hover:bg-[#0A1A2F]/90"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export to CSV
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}